import { createFileRoute, Link } from '@tanstack/react-router'
import { BrandMark } from '@/components/atoms'

export const Route = createFileRoute('/')({
  component: LandingPage,
})

const CATEGORIES: Array<[label: string, emoji: string, hint: string]> = [
  ['이공·AI', '🔬', '환경 데이터 시각화 등'],
  ['의학·생명', '🧬', '증상 자가체크 등'],
  ['인문·사회', '📚', '한국사 인포그래픽 등'],
  ['경상·경영', '📈', '가계부, 미니 쇼핑몰'],
  ['예체능', '🎨', '포트폴리오, 작곡 시각화'],
  ['교육·복지', '🤝', '학습 도우미, 또래 튜터'],
  ['진로 미정', '🧭', '나를 알아가는 검사'],
  ['자유 주제', '✨', '내 아이디어로 시작'],
]

const PRINCIPLES: Array<[num: string, title: string, body: string]> = [
  ['01', '아이디어부터 정리하기', '아이디어가 막연해도 괜찮아요. 무엇을·누구를 위해·어떤 모양인지 같이 정한 다음 코드를 짜요.'],
  ['02', '비밀 키는 자동으로 가려요', '실수로 비밀번호 같은 걸 채팅창에 붙여 넣어도 바로 가려드려요. 안전한 곳으로 옮기는 법까지 알려드려요.'],
  ['03', '파일이 생기면 이유도 함께', '새 파일이 추가될 때마다 "이 파일이 왜 필요한지"를 한 문장으로 설명해요.'],
  ['04', '먼저 묻고, 나중에 짜기', '여러 가지로 해석될 수 있을 땐 후보를 카드로 보여줘요. 학생이 직접 골라요.'],
  ['05', '꼭 필요한 만큼만', '시키지 않은 기능은 만들지 않아요. 코드 옆엔 "왜 이렇게 짰는지" 주석을 한국어로 달아요.'],
  ['06', '직접 써보고 판단하기', '코드를 한 줄씩 다 뜯어보지 않아도 돼요. 사용자처럼 직접 작동시켜 보고 "내가 원한 게 맞나" 확인하면 충분해요.'],
]

interface SampleProject {
  title: string
  se: string
  tags: string[]
  author: string
  likes: number
  comments: number
}

const SAMPLE_PROJECTS: SampleProject[] = [
  {
    title: '우리 동네로 다시 본 코로나',
    se: '생명윤리 세특 · 데이터로 본 지역 격차',
    tags: ['의학·생명', '데이터', 'RAG'],
    author: '김민서',
    likes: 24,
    comments: 8,
  },
  {
    title: '고전소설 속 한 문단 챗봇',
    se: '국어 세특 · 1830년 한양으로 떠나는 답사',
    tags: ['인문', 'RAG', '챗봇'],
    author: '이서윤',
    likes: 31,
    comments: 12,
  },
  {
    title: '우리 학교 매점 영수증 가계부',
    se: '경제 세특 · 8주 동안 내 소비 들여다보기',
    tags: ['경상', '메모리'],
    author: '박지호',
    likes: 17,
    comments: 5,
  },
]

function LandingPage() {
  return (
    <div className="bg-white">
      {/* === Hero === */}
      <header className="border-b border-border bg-white">
        <div className="mx-auto flex h-14 max-w-[1240px] items-center justify-between px-6">
          <Link to="/" className="flex items-center gap-2">
            <BrandMark size={28} />
            <span className="text-[16px] font-extrabold tracking-tight text-text">바이브타운</span>
          </Link>
          <Link
            to="/auth"
            className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-[13px] font-bold text-white transition-colors hover:bg-primary-hover"
          >
            시작하기
          </Link>
        </div>
      </header>

      <section className="mx-auto grid max-w-[1240px] grid-cols-1 items-center gap-12 px-6 py-20 md:grid-cols-[1.05fr_0.95fr] md:gap-16 md:px-16 md:py-24">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full bg-primary-soft px-3.5 py-1.5 text-[13px] font-bold tracking-tight text-primary">
            <span className="h-1.5 w-1.5 rounded-full bg-primary" />
            10대를 위한 AI 작업실
          </div>
          <h1 className="kr-tight mt-5 text-[40px] font-extrabold leading-[1.12] tracking-[-0.035em] text-text sm:text-[56px]">
            머릿속에만 있던 아이디어,
            <br />
            <span className="text-primary">한번 꺼내볼까요?</span>
          </h1>
          <p className="mt-5 max-w-[480px] text-[16px] leading-[1.55] text-text-2 sm:text-[19px]">
            아이디어만 있어도 돼요. 챗봇이랑 얘기하면서 정리하고,
            작업실에서 직접 짜보고, 친구들이 뭘 만들었는지도 구경해봐요.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-2">
            <Link
              to="/auth"
              className="inline-flex items-center justify-center rounded-xl bg-primary px-6 py-3.5 text-[16px] font-bold text-white transition-all ease-spring hover:bg-primary-hover hover:shadow-lift active:scale-[0.97]"
            >
              무료로 시작하기
            </Link>
            <Link
              to="/payment"
              className="inline-flex items-center justify-center rounded-xl px-5 py-3.5 text-[14px] font-medium text-text-3 transition-colors hover:bg-surface-alt hover:text-text-2"
            >
              단체로 쓰시는 분은 여기로
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap gap-7 text-[13px] font-medium text-text-3">
            <span>· 만 14세 미만은 보호자 동의가 필요해요</span>
            <span>· 모든 화면이 한국어예요</span>
            <span>· 졸업할 때 만든 작품 그대로 가져갈 수 있어요</span>
          </div>
        </div>

        <HeroDemo />
      </section>

      {/* === 카테고리 === */}
      <section className="mx-auto max-w-[1080px] px-6 py-20">
        <h2 className="kr-tight text-[28px] font-extrabold leading-[1.2] tracking-[-0.03em] text-text sm:text-[32px]">
          코딩 처음이어도,
          <br />관심 있는 거부터 만들어봐요
        </h2>
        <p className="mt-3 max-w-[640px] text-[15px] text-text-2">
          분야별로 시작할 수 있는 템플릿이 준비돼 있어요. 마음에 드는 걸 골라봐요.
        </p>
        <div className="cat-grid">
          {CATEGORIES.map(([t, e, s]) => (
            <div key={t} className="cat-card">
              <div className="icon-tile bg-accent-soft text-[18px]" style={{ color: 'var(--color-accent-deep)' }}>
                {e}
              </div>
              <h4>{t}</h4>
              <p>{s}</p>
            </div>
          ))}
        </div>
      </section>

      {/* === 12원칙 (spec §11.2.1 하네스 v2 — 랜딩엔 대표 6가지만 노출) === */}
      <section className="principles">
        <div className="principles-inner">
          <h2 className="kr-tight text-[28px] font-extrabold leading-[1.2] tracking-[-0.03em] text-text sm:text-[32px]">
            AI가 코드부터
            <br />
            짜주지 않는 이유
          </h2>
          <p className="mt-3 text-[15px] text-text-2">바이브타운은 학생이 먼저 생각하도록 12가지 원칙을 지켜요. 그중 대표 6가지를 소개할게요.</p>
          <div className="p-grid">
            {PRINCIPLES.map(([n, t, d]) => (
              <div key={n} className="p-card">
                <div className="num">원칙 {n}</div>
                <h5>{t}</h5>
                <p>{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === 친구들 작품 미리보기 === */}
      <section className="mx-auto max-w-[1080px] px-6 py-20">
        <h2 className="kr-tight text-[28px] font-extrabold leading-[1.2] tracking-[-0.03em] text-text sm:text-[32px]">
          친구들이 만든 작품도
          <br />
          한번 구경해봐요
        </h2>
        <p className="mt-3 max-w-[640px] text-[15px] text-text-2">
          AI한테 보낸 메시지 전체는 공개되지 않아요. 대신 어디서 막혔고, 어떻게 풀었고, 무엇을 배웠는지를 나눠요.
        </p>
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {SAMPLE_PROJECTS.map((c) => (
            <article key={c.title} className="archive-card relative">
              <span className="absolute top-3 left-3 z-10 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-text-3 shadow-sm">
                예시 작품
              </span>
              <div className="archive-thumb">
                <div className="tool-badges">
                  <div className="tool-badge">A</div>
                  <div className="tool-badge">V</div>
                </div>
                <div className="preview-mini">
                  <div style={{ height: 6, width: '70%', background: 'var(--color-primary-soft)', borderRadius: 3 }} />
                  <div style={{ height: 5, width: '90%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                  <div style={{ height: 5, width: '60%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                  <div style={{ height: 5, width: '75%', background: 'var(--color-surface-alt)', borderRadius: 3 }} />
                </div>
              </div>
              <div className="archive-card-body">
                <h4>{c.title}</h4>
                <div className="se-line">{c.se}</div>
                <div className="archive-tags">
                  {c.tags.map((t) => (
                    <span key={t} className="inline-flex items-center rounded-full bg-primary-soft px-2.5 py-1 text-[11px] font-semibold text-primary">
                      #{t}
                    </span>
                  ))}
                </div>
                <div className="archive-meta">
                  <span>❤ {c.likes}</span>
                  <span>💬 {c.comments}</span>
                  <span className="author">by {c.author}</span>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* === Final CTA === */}
      <section className="bg-surface-alt">
        <div className="mx-auto max-w-[680px] px-6 py-20 text-center">
          <h2 className="kr-tight text-[28px] font-extrabold leading-[1.2] tracking-[-0.03em] text-text sm:text-[32px]">
            첫 작품,
            <br />한번 만들어볼까요?
          </h2>
          <p className="mt-3 text-[15px] text-text-2">첫 번째 작품은 결제 없이 무료로 만들어볼 수 있어요.</p>
          <Link
            to="/auth"
            className="mt-7 inline-flex items-center justify-center rounded-xl bg-primary px-7 py-3.5 text-[16px] font-bold text-white transition-all ease-spring hover:bg-primary-hover hover:shadow-lift active:scale-[0.97]"
          >
            지금 시작하기
          </Link>
        </div>
      </section>

      <footer className="border-t border-border bg-white py-10 text-center text-[12px] text-text-3">
        © 2026 Vibetown · 바이브타운
      </footer>
    </div>
  )
}

function HeroDemo() {
  return (
    <div className="overflow-hidden rounded-stage border border-border bg-white shadow-float" style={{ aspectRatio: '16 / 11' }}>
      <div className="flex h-7 items-center gap-1.5 border-b border-border bg-[#F4F6FA] px-2.5">
        <span className="h-2 w-2 rounded-full bg-[#FF5F57]" />
        <span className="h-2 w-2 rounded-full bg-[#FFBD2E]" />
        <span className="h-2 w-2 rounded-full bg-[#28C840]" />
        <span className="ml-2 rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-text-3">student-minseo0421.vibetown.co.kr</span>
      </div>
      <div className="grid h-[calc(100%-28px)] grid-cols-[1fr_1fr]">
        <div className="flex flex-col gap-2 border-r border-border bg-surface-alt p-3">
          <div className="self-start max-w-[90%] rounded-xl bg-white px-3 py-2 text-[11px] leading-[1.45] text-text shadow-sm">
            오, 미세먼지 챗봇이네요. 누가 쓸 작품인지부터 같이 정해볼까요?
          </div>
          <div className="self-end max-w-[80%] rounded-xl bg-primary-soft px-3 py-2 text-[11px] leading-[1.45] text-text">
            우리 학교 친구들이요
          </div>
          <div className="self-start max-w-[90%] rounded-xl bg-white px-3 py-2 text-[11px] leading-[1.45] text-text shadow-sm">
            좋아요. 먼저 정해야 할 게 네 가지 있어요. 같이 볼까요?
          </div>
          <div className="self-start mt-1 inline-flex items-center gap-1.5 rounded-full bg-[rgba(244,180,0,0.15)] px-2.5 py-1 text-[10px] font-semibold" style={{ color: '#7A5A00' }}>
            <span className="inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-warning" />
            첫 화면 만들고 있어요
          </div>
        </div>
        <div className="flex flex-col gap-2 p-3">
          <div className="text-[10px] uppercase tracking-wider text-text-3">미리보기</div>
          <div className="flex flex-col gap-1.5 rounded-lg border border-border bg-white p-3">
            <h4 className="m-0 text-[13px] font-bold text-text">오늘의 미세먼지</h4>
            <div className="text-[11px] text-text-2">
              <span style={{ color: 'var(--color-success)' }}>●</span> 서울 강남구 · 좋음 (PM2.5 12)
            </div>
            <div className="text-[11px] text-text-2">
              <span style={{ color: 'var(--color-warning)' }}>●</span> 경기 안양시 · 보통 (PM2.5 28)
            </div>
            <div className="text-[11px] text-text-2">
              <span style={{ color: 'var(--color-success)' }}>●</span> 부산 해운대구 · 좋음 (PM2.5 9)
            </div>
            <button className="mt-1 self-start rounded-md bg-primary px-2.5 py-1 text-[10px] font-bold text-white">새로고침</button>
          </div>
        </div>
      </div>
    </div>
  )
}
